// Words with their web addresses live (Features 2, 21 Oct): one piece every board uses to show what someone
// typed, pasted or an agent wrote, so a link opens with a click everywhere and the same way.
//
// - <Linked text /> draws the words, each address a link by a short name (src/lib/links.ts says what counts as
//   one, and its name): the whole address shows on hover, and a copy takes the words as written (Round 7).
// - useLinkField is for words that are their own field (a post's text, a deal's next step): while nobody is
//   typing, a field with an address in it shows the words with the link live; a click anywhere else in them
//   starts typing exactly there, and Tab stops on each link, then goes on into the field (Round 7).

import { Fragment, useCallback, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent } from "react";
import { flushSync } from "react-dom";
import { hasLink, linkParts, type LinkPart } from "./links";

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

/** Where the last press on a link began, to tell a click from a drag that began on one. */
let pressed: { x: number; y: number } | null = null;

/**
 * A press on a link is the link's: it never starts a drag, selects its tile, opens what the row opens or starts
 * writing, and a right click gives the browser's own menu for a link (open, copy its address). A link is never
 * dragged itself (dropped on the board it would make an embed), and a press that moved away before letting go
 * was someone reaching for the card under it, so it opens nothing.
 */
export const linkGuard = {
  draggable: false,
  onPointerDown: (e: PointerEvent) => {
    stop(e);
    pressed = { x: e.clientX, y: e.clientY };
  },
  onMouseDown: (e: MouseEvent) => stop(e),
  onClick: (e: MouseEvent) => {
    stop(e);
    // a click from the keyboard (Enter) has no press to measure
    if (e.detail > 0 && pressed && Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) > 8) e.preventDefault();
    pressed = null;
  },
  onDoubleClick: (e: MouseEvent) => stop(e),
  onContextMenu: (e: MouseEvent) => stop(e),
  // Enter opens the link, never what holds it (a card that opens its deal on Enter)
  onKeyDown: (e: KeyboardEvent) => e.key === "Enter" && stop(e),
};

/**
 * What a link drawn by a short name carries (Round 7): the whole address on hover, and the words as written
 * (data-written), which a copy takes and a click beside it counts, when they are not what it shows.
 */
export const linkMarks = (href: string, shown: string, written: string) => ({ title: href, ...(shown !== written ? { "data-written": written } : {}) });

/** The words, each web address in them a link that opens in a new tab, by its short name. Words with no address are drawn as they are. */
export function Linked({ text }: { text: string | null | undefined }) {
  const parts = linkParts(text);
  if (!parts.some((p) => p.href)) return <>{text ?? ""}</>;
  return (
    <>
      {parts.map((p: LinkPart, i) =>
        p.href ? (
          <a key={i} className="lk" href={p.href} target="_blank" rel="noopener noreferrer" data-interactive {...linkMarks(p.href, p.label ?? p.text, p.text)} {...linkGuard}>
            {p.label ?? p.text}
          </a>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * Copying words with a link drawn by a short name copies the words as written, the address and all, never the
 * short name (Round 7): what goes on the clipboard is what was saved. A rich place (a doc, an email) gets the
 * links as links. Selected words with no such link are copied as the browser copies them.
 */
function copyWritten(e: ClipboardEvent) {
  const sel = document.getSelection();
  if (!e.clipboardData || !sel || sel.isCollapsed || !sel.rangeCount) return;
  // a field's own words are copied as typed
  if ((e.target as Element | null)?.closest?.("input, textarea, [contenteditable]:not([contenteditable='false'])")) return;
  const range = sel.getRangeAt(0);
  const top = range.commonAncestorContainer;
  const at = top.nodeType === Node.ELEMENT_NODE ? (top as Element) : top.parentElement;
  // the selection inside one link: its words as written
  const inside = at?.closest<HTMLElement>("a[data-written]");
  if (inside) {
    e.clipboardData.setData("text/plain", inside.dataset.written ?? "");
    e.clipboardData.setData("text/html", inside.outerHTML);
    e.preventDefault();
    return;
  }
  if (!at || ![...at.querySelectorAll("a[data-written]")].some((a) => range.intersectsNode(a))) return;
  // words only: a widget's frame, a picture or a script selected with them is never loaded or kept again
  const copy = () => {
    const f = range.cloneContents();
    for (const n of f.querySelectorAll("iframe, frame, object, embed, script, style, img, video, audio, canvas")) n.remove();
    return f;
  };
  const html = document.createElement("div");
  html.append(copy());
  // the words as the page reads them, each short name swapped for its words as written; laid out off screen,
  // so a paragraph or a line in a list still ends its line
  const words = document.createElement("div");
  words.style.cssText = "position:fixed;left:-99999px;top:0;white-space:pre-wrap";
  words.append(copy());
  for (const a of words.querySelectorAll<HTMLElement>("a[data-written]")) a.replaceWith(a.textContent ? (a.dataset.written ?? "") : "");
  document.body.append(words);
  const text = words.innerText;
  words.remove();
  e.clipboardData.setData("text/plain", text);
  e.clipboardData.setData("text/html", html.innerHTML);
  e.preventDefault();
}
if (typeof document !== "undefined") document.addEventListener("copy", copyWritten);

const isLink = (t: EventTarget | null) => !!(t as Element | null)?.closest?.("a[href]");

/** How far into the words a point is, as a character count, or null when the point is not on them. */
function caretAt(root: HTMLElement, x: number, y: number): number | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  let offset = 0;
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    if (p) [node, offset] = [p.offsetNode, p.offset];
  } else if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) [node, offset] = [r.startContainer, r.startOffset];
  }
  if (!node || !root.contains(node)) return null;
  const r = document.createRange();
  r.setStart(root, 0);
  r.setEnd(node, offset);
  // a link drawn by a short name counts as its words as written, which the field holds (Round 7)
  const before = r.cloneContents();
  for (const a of before.querySelectorAll<HTMLElement>("a[data-written]")) a.replaceWith(a.textContent ? (a.dataset.written ?? "") : "");
  return before.textContent?.length ?? 0;
}

/**
 * For words that are their own field. `view` says to draw the words (with <Linked />, spread `viewProps` on what
 * holds them) in place of the field; otherwise draw the field with `ref` and call `fieldProps`' focus and blur
 * from its own. A field with no address in it is always the field, exactly as before; a read-only one with an
 * address is always the words.
 */
export function useLinkField<T extends HTMLInputElement | HTMLTextAreaElement>(value: string, readOnly = false) {
  const [typing, setTyping] = useState(false);
  const field = useRef<T | null>(null);
  // the field itself. Taken away while it is typed in (a card closed with Escape goes without a blur), the typing is
  // over, so the next words with an address in them show their links again (Round 7: the planner's task card)
  const ref = useCallback((el: T | null) => {
    field.current = el;
    if (!el) setTyping(false);
  }, []);
  const view = !typing && hasLink(value);
  /** start typing with the caret at a character, or at the end */
  const edit = (at: number | null) => {
    if (readOnly) return;
    // drawn now, inside the click, so a phone opens its keyboard for it
    flushSync(() => setTyping(true));
    const el = field.current;
    if (!el) return;
    el.focus();
    const n = Math.min(at ?? el.value.length, el.value.length);
    try {
      el.setSelectionRange(n, n);
    } catch {
      // a field type with no caret (email) keeps the browser's own
    }
  };
  return {
    view,
    /** to give the field: <textarea ref={lf.ref} /> */
    ref,
    /** the field while it is drawn, to measure or move */
    field,
    viewProps: {
      // the words take focus only from code; Tab stops on each of their links first (Round 7)
      tabIndex: readOnly ? undefined : -1,
      "data-link-view": "",
      // a press on the words is kept from focusing them, so the click below puts the caret where it landed
      onMouseDown: (e: MouseEvent) => !readOnly && !isLink(e.target) && e.preventDefault(),
      onClick: (e: MouseEvent<HTMLElement>) => !isLink(e.target) && edit(caretAt(e.currentTarget, e.clientX, e.clientY)),
      // the code that moves on to the field lands on the words: typing starts at their end
      onFocus: (e: FocusEvent<HTMLElement>) => e.target === e.currentTarget && edit(null),
      // Tab on the last link goes on into the field, typing at the end, as Tab into a field always did
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (readOnly || e.key !== "Tab" || e.shiftKey || !isLink(e.target)) return;
        const links = e.currentTarget.querySelectorAll("a[href]");
        if (e.target !== links[links.length - 1]) return;
        e.preventDefault();
        edit(null);
      },
    },
    fieldProps: {
      onFocus: () => setTyping(true),
      onBlur: () => setTyping(false),
    },
  };
}
